import * as React from 'react';

import { useCopyToClipboard } from '@uidotdev/usehooks';
import { XIcon } from 'lucide-react';
import {
  Button as AriaButton,
  Tag as AriaTag,
  TagGroup as AriaTagGroup,
  TagGroupProps as AriaTagGroupProps,
  TagList as AriaTagList,
  TagListProps as AriaTagListProps,
  TagProps as AriaTagProps,
  TagRenderProps,
  composeRenderProps,
  Text,
} from 'react-aria-components';

import { cn, withColor } from '../../../lib/stylingUtils';
import { ThemeColor } from '../../../types/theme';
import { Label } from '../Field';

export function TagList<T extends object>({
  className,
  ...props
}: AriaTagListProps<T>) {
  return (
    <AriaTagList
      className={composeRenderProps(className, (className) =>
        cn(
          '@TagList',
          'h-input flex overflow-clip rounded-md border border-neutral/40',
          className,
        ),
      )}
      {...props}
    />
  );
}

export interface TagProps
  extends
    Omit<AriaTagProps, 'onClick'>,
    Pick<React.HTMLAttributes<HTMLDivElement>, 'onClick'> {
  ref?: React.Ref<HTMLDivElement>;
  size?: 'xs' | 'small' | 'default' | 'large';
  color?: ThemeColor;
}

export const Tag = ({
  children,
  className,
  ref,
  onClick,
  ...props
}: TagProps) => {
  const textValue = typeof children === 'string' ? children : undefined;

  return (
    <AriaTag
      textValue={textValue}
      className={composeRenderProps(
        cn('@Tag', className),
        (className, renderProps: TagRenderProps) =>
          cn(
            'flex grow cursor-pointer items-center justify-center px-3 text-sm',
            renderProps.isSelected && 'bg-primary text-white',
            renderProps.allowsRemoving && 'pr-1',
            className,
          ),
      )}
      {...props}
    >
      {composeRenderProps(
        children,
        (
          children,
          renderProps: TagRenderProps & {
            defaultChildren: React.ReactNode | undefined;
          },
        ) => (
          <div ref={ref} role="button" onClick={onClick}>
            {children}
            {renderProps.allowsRemoving && (
              <AriaButton slot="remove" className={cn(className)}>
                <XIcon aria-hidden className="size-3" />
              </AriaButton>
            )}
          </div>
        ),
      )}
    </AriaTag>
  );
};

export interface TagGroupProps<T>
  extends
    Omit<
      AriaTagGroupProps,
      'children' | 'onSelectionChange' | 'selectedKeys' | 'selectionBehavior'
    >,
    Pick<AriaTagListProps<T>, 'items' | 'children' | 'renderEmptyState'> {
  label?: string;
  description?: string;
  errorMessage?: string;
  isRequired?: boolean;
  isDisabled?: boolean;
  onSelectionChange?: (value: string[]) => void;
  selectedKeys?: string[];
}

export function TagGroup<T extends { label: string; key: string }>({
  label,
  description,
  className,
  errorMessage,
  items,
  renderEmptyState,
  isRequired,
  isDisabled,
  selectionMode = 'single',
  ...props
}: TagGroupProps<T>) {
  return (
    <AriaTagGroup
      {...props}
      className={cn(
        '@TagGroup',
        'group flex flex-col gap-2',
        isDisabled && 'pointer-events-none opacity-50',
        className,
      )}
      selectionMode={selectionMode}
      onSelectionChange={(v: 'all' | Set<string | number>) => {
        if (isDisabled) {
          return;
        }

        if (
          typeof v !== 'string' &&
          v.size === 0 &&
          selectionMode === 'single'
        ) {
          return;
        }

        props.onSelectionChange?.(
          typeof v === 'string'
            ? Array.from(items ?? []).map((item) => item.key)
            : (Array.from(v) as string[]),
        );
      }}
    >
      {label && <Label aria-required={isRequired}>{label}</Label>}
      <TagList items={items} renderEmptyState={renderEmptyState}>
        {(item: T) => <Tag>{item.label}</Tag>}
      </TagList>
      {description && (
        <Text className="text-sm text-neutral/60" slot="description">
          {description}
        </Text>
      )}
      {errorMessage && (
        <Text className="text-sm text-failure" slot="errorMessage">
          {errorMessage}
        </Text>
      )}
    </AriaTagGroup>
  );
}

export type BadgeProps = Pick<
  TagProps,
  'color' | 'size' | 'className' | 'className'
> &
  Pick<React.HTMLAttributes<HTMLDivElement>, 'onClick'> & {
    ref?: React.Ref<HTMLDivElement>;
  };

export const Badge = ({
  className,
  color = 'neutral',
  children,
  size = 'small',
  ...props
}: BadgeProps & React.PropsWithChildren) => {
  const styles = {
    ...(color ? withColor(color) : {}),
  } as React.CSSProperties;

  return (
    <div
      className={cn(
        '@Badge',
        'inline-flex cursor-default items-center gap-2 rounded-sm border font-medium ring-offset-neutral/10',
        'transition-colors',
        /* Focus */
        'rac-focus:ring-2 rac-focus:ring-primary/40 rac-focus:ring-offset-2 rac-focus:outline-none',
        /* Disabled */
        'rac-disabled:cursor-not-allowed rac-disabled:opacity-50',
        /* Colors applied dynamically */
        'border-custom-color/20 bg-custom-color/15 text-custom-color',
        /* Selected */
        'rac-selected:border-primary-darker',
        /* Hover */
        'rac-hover:bg-custom-color/80',

        size === 'default' && 'px-2.5 py-0.5 text-sm',
        size === 'xs' && 'px-0.5 py-0.25 text-[0.7em]',
        size === 'small' && 'px-1 py-0.25 text-xs',
        size === 'large' && 'px-2.5 py-0.5 text-base',

        className,
      )}
      {...props}
      style={styles}
    >
      {children}
    </div>
  );
};

export interface IDTagProps extends Pick<
  TagProps,
  'color' | 'className' | 'className' | 'size'
> {
  onCopyContents?: (content: string) => void;
}

export const IDTag = ({
  className,
  // color = 'neutral',
  size = 'small',
  onCopyContents,
  children,
  ...props
}: IDTagProps & React.PropsWithChildren) => {
  const ref = React.useRef<HTMLDivElement>(null);
  const [, copy] = useCopyToClipboard();

  const handleClick = async (_e: React.MouseEvent<HTMLDivElement>) => {
    try {
      const contents = ref?.current?.innerText;
      if (!contents) return;

      await copy(contents);
      onCopyContents?.(contents);
    } catch (error) {
      console.error('Failed to copy to clipboard:', error);
    }
  };

  return (
    <Badge
      ref={ref}
      className={cn('@IDTag', 'font-mono hover:bg-warning/20', className)}
      onClick={handleClick}
      size={size}
      {...props}
    >
      {children}
    </Badge>
  );
};
